/**
 * BASE DE DATOS LOCAL DEL DISPOSITIVO (IndexedDB con Dexie).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En un negocio real el internet se cae. Y cuando se cae, la venta **igual
 * ocurre**: el cliente entrega el dinero y se lleva el producto. Un sistema que
 * en ese momento dice "sin conexión" obliga al cajero a anotar en un papel — y
 * ese papel se pierde.
 *
 * Softfi guarda en el propio dispositivo:
 *   · el catálogo, las monedas con sus tasas y los métodos de pago  → para poder VENDER
 *   · los clientes                                                   → para poder FIAR
 *   · las ventas y los clientes hechos sin conexión                  → para no PERDERLOS
 *
 * Cuando vuelve el internet, todo eso se envía solo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ LOS CLIENTES TAMBIÉN, Y POR QUÉ SE PUEDEN CREAR SIN SEÑAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo contó el dueño después de usarlo sin internet: «si tenemos la
 * configuración de que los clientes sean obligatorios está dañado porque no
 * salen, hay que crear, y eso hace que se buguee si creamos otro cliente».
 *
 * Y tenía razón por partida doble. Sin señal el buscador de clientes preguntaba
 * al servidor y devolvía la lista vacía, así que TODOS los clientes del negocio
 * dejaban de existir para el cajero. Y el formulario de registrar tampoco
 * llegaba a ninguna parte, con lo cual, con los clientes obligatorios, no se
 * podía cobrar. Sin internet. Que es cuando más falta hace.
 *
 * Ahora el buscador lee de aquí siempre —con señal y sin ella— y registrar
 * escribe aquí primero. El envío al servidor viene después, cuando se pueda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DECISIÓN: LA CACHÉ SE BORRA AL CAMBIAR DE NEGOCIO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Guardamos el id del negocio activo en `meta`. Si al abrir la app el id es otro
 * (el usuario cambió de negocio), se borra TODO y se vuelve a descargar.
 *
 * Es más agresivo que filtrar por negocio en cada consulta, y a propósito: un
 * error de filtrado aquí mostraría los productos de un negocio en la caja de
 * otro. Borrar y recargar es imposible de equivocar.
 */

import type { HuellasGuardadas } from "@/features/pos/porHuellas";
import Dexie, { type Table } from "dexie";
import type { VentaIn } from "./endpoints";
import type {
  Customer,
  ItemListRow,
  PaymentMethod,
  Sale,
  TenantCurrency,
} from "./types";

/**
 * Un cliente registrado en el dispositivo, esperando a llegar al servidor.
 *
 * ⚠️ SU `id` LO ELIGIÓ EL DISPOSITIVO, Y ES EL DEFINITIVO.
 *
 * Ese id es el que apunta la venta guardada de la misma tarde. Si el servidor
 * le pusiera otro al recibirlo, esa venta llegaría nombrando a un cliente que
 * no existe — así que el servidor acepta el id propuesto (ver `client_uuid` en
 * `CustomerIn`, del lado del servidor).
 *
 * Hay un caso en que el id CAMBIA igualmente y hay que estar preparado: que esa
 * cédula ya estuviera registrada. Entonces el servidor devuelve el cliente de
 * siempre —con su propio id— y aquí se corrigen las ventas que apuntaban al
 * nuevo. Ver `reemplazarClienteEnLaCola`.
 */
export type ClientePendiente = {
  /** El mismo id con el que se guardó en `clientes`. */
  id: string;
  /** Lo que se le enviará al servidor, tal cual. */
  datos: {
    client_uuid: string;
    name: string;
    phone?: string | null;
    tax_id_tipo?: string | null;
    tax_id?: string | null;
    address?: string | null;
    /** Solo si el negocio exige el correo (Clientes → Opciones). */
    email?: string | null;
  };
  creado_en: string;
  intentos: number;
  ultimo_error: string | null;
};

/** Una venta esperando a que vuelva el internet. */
export type VentaPendiente = {
  /** Generado por el dispositivo. Es la llave de la idempotencia. */
  client_uuid: string;
  /** Lo que se le enviará al servidor, tal cual. */
  payload: VentaIn;
  /** Recibo armado localmente, para poder imprimirlo AHORA mismo. */
  recibo: Sale;
  creado_en: string;
  intentos: number;
  ultimo_error: string | null;
};

/**
 * Una venta que el servidor RECHAZÓ.
 *
 * Nunca se descarta en silencio. Si el dueño borró un producto mientras el
 * celular estaba sin señal, esa venta cae aquí con el motivo para que una persona
 * decida qué hacer.
 */
export type VentaEnConflicto = {
  client_uuid: string;
  payload: VentaIn;
  recibo: Sale;
  error_code: string;
  error_message: string;
  fecha: string;
};

type Meta = { clave: string; valor: string };

class SoftfiDB extends Dexie {
  items!: Table<ItemListRow, string>;
  monedas!: Table<TenantCurrency, string>;
  metodos!: Table<PaymentMethod, string>;
  clientes!: Table<Customer, string>;
  clientesPendientes!: Table<ClientePendiente, string>;
  pendientes!: Table<VentaPendiente, string>;
  conflictos!: Table<VentaEnConflicto, string>;
  meta!: Table<Meta, string>;

  constructor() {
    super("softfi");

    // Los índices declarados son los que se usan para buscar. `barcode` está
    // indexado porque el escáner busca por ahí y tiene que responder al instante.
    this.version(1).stores({
      items: "id, barcode, name, kind, category_id",
      monedas: "currency_code, is_primary",
      metodos: "id, is_active",
      pendientes: "client_uuid, creado_en",
      conflictos: "client_uuid, fecha",
      meta: "clave",
    });

    /*
      ⚠️ Una versión NUEVA, no un cambio en la de arriba.

      Dexie usa el número para saber qué hacer con las bases que ya existen en
      los dispositivos. Tocando la versión 1, el navegador de un cajero que ya
      tiene Softfi instalado se queda con la estructura vieja y sin las tablas
      nuevas — y falla al leerlas, sin decir por qué.

      Añadiendo la 2, Dexie migra solo. Las tablas de la 1 siguen tal cual y no
      hay que repetirlas.
    */
    this.version(2).stores({
      clientes: "id, name, tax_id, phone",
      clientesPendientes: "id, creado_en",
    });
  }
}

export const db = new SoftfiDB();

// =============================================================================
//  META (id del negocio, última sincronización)
// =============================================================================

const LLAVE_NEGOCIO = "tenant_id";
const LLAVE_ACTUALIZADO = "catalogo_actualizado_en";
/** De qué sucursal es el catálogo guardado. Ver `asegurarSucursal`. */
const LLAVE_SUCURSAL = "sucursal_de_la_copia";

async function leerMeta(clave: string): Promise<string | null> {
  const fila = await db.meta.get(clave);
  return fila?.valor ?? null;
}

async function escribirMeta(clave: string, valor: string): Promise<void> {
  await db.meta.put({ clave, valor });
}

/**
 * Las huellas de la última vez que se renovó una copia. Ver `porHuellas.ts`.
 *
 * `null` si no hay, o si lo guardado no se puede leer: en los dos casos lo
 * correcto es bajarlo todo, que es lo que se hacía siempre.
 */
export async function leerHuellas(
  de: "catalogo" | "clientes",
): Promise<HuellasGuardadas | null> {
  const texto = await leerMeta(`huellas-${de}`);
  if (!texto) return null;
  try {
    return JSON.parse(texto) as HuellasGuardadas;
  } catch {
    return null;
  }
}

/** `null` las borra: la próxima vez se baja todo. */
export async function guardarHuellas(
  de: "catalogo" | "clientes",
  huellas: HuellasGuardadas | null,
): Promise<void> {
  if (huellas === null) await db.meta.delete(`huellas-${de}`);
  else await escribirMeta(`huellas-${de}`, JSON.stringify(huellas));
}

/**
 * Borra la caché de datos del negocio (no las ventas pendientes).
 *
 * Las ventas pendientes NO se tocan nunca: son dinero que ya se cobró. Si el
 * usuario cambia de negocio con ventas sin enviar, se envían igual porque el
 * `payload` ya lleva todo lo necesario.
 */
export async function limpiarCacheDeDatos(): Promise<void> {
  /*
    ⚠️ Los clientes SÍ se borran y los clientes PENDIENTES no.

    Es la misma regla que con las ventas: la copia se puede volver a bajar, pero
    lo que se registró aquí y no ha llegado al servidor no existe en ningún otro
    sitio. Borrarlo sería perder a la señora que se acaba de llevar mercancía
    fiada.
  */
  await Promise.all([
    db.items.clear(),
    db.monedas.clear(),
    db.metodos.clear(),
    db.clientes.clear(),
    // Sin la copia, sus huellas no dicen nada: la próxima vez, todo.
    db.meta.delete("huellas-catalogo"),
    db.meta.delete("huellas-clientes"),
    db.meta.delete(LLAVE_SUCURSAL),
  ]);
}

/**
 * Si la copia guardada es de OTRA sucursal, tira su catálogo y sus métodos de
 * pago (2026-09-29).
 *
 * Cada sucursal tiene su catálogo, sus precios, su existencia y sus métodos.
 * Sin esto, al cambiar de sucursal la caja pintaba primero la copia de la otra
 * —y sin internet se quedaba con ella, vendiendo con precios que aquí no son—.
 * Los clientes y las monedas se quedan: son del negocio entero.
 *
 * ⚠️ Una copia sin sucursal anotada (guardada antes de esto) se da por buena:
 * se bajó con la sucursal de entonces, que es la de ahora. Tirarla dejaría sin
 * catálogo a una caja que se actualizó y justo después perdió la señal.
 */
export async function asegurarSucursal(sucursal: string | null): Promise<void> {
  const actual = sucursal ?? "";
  const guardada = await leerMeta(LLAVE_SUCURSAL);
  if (guardada === actual) return;
  if (guardada !== null) {
    await Promise.all([
      db.items.clear(),
      db.metodos.clear(),
      // Sin la copia, sus huellas no dicen nada: la próxima vez, todo.
      db.meta.delete("huellas-catalogo"),
    ]);
  }
  await escribirMeta(LLAVE_SUCURSAL, actual);
}

/** Si el negocio activo cambió, borra la caché para no mezclar catálogos. */
export async function asegurarNegocio(tenantId: string): Promise<void> {
  const guardado = await leerMeta(LLAVE_NEGOCIO);
  if (guardado !== tenantId) {
    await limpiarCacheDeDatos();
    await escribirMeta(LLAVE_NEGOCIO, tenantId);
    await escribirMeta(LLAVE_ACTUALIZADO, "");
  }
}

// =============================================================================
//  GUARDAR Y LEER LA FOTO DEL NEGOCIO
// =============================================================================

/**
 * Guarda en el dispositivo todo lo que el POS necesita para vender sin internet.
 *
 * Se reemplaza completo en vez de mezclar: si el dueño borró un producto, tiene
 * que desaparecer también del celular del cajero.
 */
export async function guardarFoto(datos: {
  items: ItemListRow[];
  monedas: TenantCurrency[];
  metodos: PaymentMethod[];
  /**
   * Los clientes del negocio.
   *
   * ⚠️ Opcional: si la descarga de clientes falla pero la del catálogo sale
   * bien, se guarda el catálogo y **no se toca la lista de clientes que ya
   * había**. Reemplazarla por una vacía dejaría al cajero sin poder fiarle a
   * nadie por un fallo que no tiene nada que ver.
   */
  clientes?: Customer[];
}): Promise<void> {
  // ⚠️ Las tablas en una lista, no sueltas: Dexie solo acepta cinco sueltas y
  // aquí van seis. Con la lista no hay tope.
  await db.transaction(
    "rw",
    [db.items, db.monedas, db.metodos, db.clientes, db.clientesPendientes, db.meta],
    async () => {
      await db.items.clear();
      await db.items.bulkPut(datos.items);

      await db.monedas.clear();
      await db.monedas.bulkPut(datos.monedas);

      await db.metodos.clear();
      await db.metodos.bulkPut(datos.metodos);

      if (datos.clientes) await ponerClientes(datos.clientes);

      await db.meta.put({ clave: LLAVE_ACTUALIZADO, valor: new Date().toISOString() });
    },
  );
}

/**
 * Reemplaza los clientes del aparato por los del servidor, sin perder los que
 * se registraron aquí y aún no han salido.
 *
 * Va aparte del catálogo (`guardarClientes`) porque ya no tienen tope: en un
 * negocio con decenas de miles tardan más en llegar que el catálogo, y la caja
 * no tiene por qué esperarlos para empezar a vender.
 */
export async function guardarClientes(clientes: Customer[]): Promise<void> {
  await db.transaction("rw", db.clientes, db.clientesPendientes, () => ponerClientes(clientes));
}

/** Lo de `guardarClientes`, dentro de una transacción que ya esté abierta. */
async function ponerClientes(clientes: Customer[]): Promise<void> {
  await db.clientes.clear();
  await db.clientes.bulkPut(clientes);

  /*
    ⚠️ LOS QUE AÚN NO HAN SALIDO DE AQUÍ SE VUELVEN A PONER.

    La lista del servidor no los trae —todavía no los conoce— y el
    `clear()` de arriba se los llevaría por delante. Sin esto, registrar a
    doña Carmen sin señal y que el catálogo se refrescara un segundo
    después la haría desaparecer de la pantalla con ella delante.
  */
  const enCola = await db.clientesPendientes.toArray();
  if (enCola.length > 0) {
    const suyos = await db.clientes.bulkGet(enCola.map((c) => c.id));
    await db.clientes.bulkPut(
      enCola.map(
        (c, i) =>
          suyos[i] ?? {
            id: c.id,
            name: c.datos.name,
            phone: c.datos.phone ?? null,
            email: null,
            tax_id_tipo: c.datos.tax_id_tipo ?? null,
            tax_id: c.datos.tax_id ?? null,
            address: c.datos.address ?? null,
            notes: null,
            is_active: true,
            lista_precios_id: null,
            lista_precios_nombre: null,
          },
      ),
    );
  }
}

/**
 * Guarda unos pocos productos del catálogo, sin tocar el resto.
 *
 * Para la existencia que baja al vender (ver `restarLoVendido`). Reescribir el
 * catálogo entero por un chicle sería absurdo; y sin guardarlo, al recargar la
 * página sin señal la esquina volvería al número de antes de la venta.
 */
/** Unos productos del catálogo guardado, por su id. Los que no estén vienen vacíos. */
export async function leerProductos(ids: string[]): Promise<(ItemListRow | undefined)[]> {
  if (ids.length === 0) return [];
  return db.items.bulkGet(ids);
}

/**
 * Solo las monedas, con su tasa: lo que refresca la Caja mientras está abierta.
 *
 * ⚠️ Se guardan también en el aparato, no solo en pantalla: una venta que se
 * cobre sin internet un rato después tiene que llevarse la tasa nueva, no la
 * de la mañana. Ver `refrescarTasas` en `useDatosPos`.
 */
export async function guardarMonedas(monedas: TenantCurrency[]): Promise<void> {
  await db.transaction("rw", db.monedas, async () => {
    await db.monedas.clear();
    await db.monedas.bulkPut(monedas);
  });
}

export async function guardarProductos(items: ItemListRow[]): Promise<void> {
  if (items.length === 0) return;
  await db.items.bulkPut(items);
}

export async function leerFoto(): Promise<{
  items: ItemListRow[];
  monedas: TenantCurrency[];
  metodos: PaymentMethod[];
  actualizadoEn: string | null;
}> {
  const [items, monedas, metodos, actualizado] = await Promise.all([
    db.items.toArray(),
    db.monedas.toArray(),
    db.metodos.toArray(),
    leerMeta(LLAVE_ACTUALIZADO),
  ]);

  /*
    ⚠️ EN EL MISMO ORDEN QUE LOS MANDA EL SERVIDOR.

    El aparato devuelve cada tabla ordenada por su llave, no por el orden en
    que se guardó: las monedas por código (COP antes que VES) y los métodos
    por su identificador. Al abrir Vender, la copia se pinta un segundo antes
    que la del servidor, y en ese segundo la moneda de al lado de cada precio
    era la primera que no fuera la principal —pesos colombianos en vez de
    bolívares— hasta que llegaba la buena y cambiaba sola.
  */
  const porOrden = (a: { display_order?: number | null }, b: { display_order?: number | null }) =>
    (a.display_order ?? 100) - (b.display_order ?? 100);

  return {
    items,
    monedas: [...monedas].sort(
      (a, b) => porOrden(a, b) || a.currency_code.localeCompare(b.currency_code),
    ),
    metodos: [...metodos].sort((a, b) => porOrden(a, b) || a.name.localeCompare(b.name)),
    actualizadoEn: actualizado || null,
  };
}

/** ¿Hay datos suficientes para poder vender sin internet? */
export async function puedeVenderSinConexion(): Promise<boolean> {
  const [items, metodos] = await Promise.all([db.items.count(), db.metodos.count()]);
  return items > 0 && metodos > 0;
}

// =============================================================================
//  CLIENTES EN EL DISPOSITIVO
// =============================================================================


/** Todos los clientes guardados, para buscar en memoria. */
export async function clientesGuardados(): Promise<Customer[]> {
  return db.clientes.toArray();
}

/** Un cliente de la copia del aparato, o `undefined` si no está. */
export async function leerClienteLocal(id: string): Promise<Customer | undefined> {
  return db.clientes.get(id);
}

export async function contarClientesGuardados(): Promise<number> {
  return db.clientes.count();
}

/**
 * Registra un cliente EN EL DISPOSITIVO y lo pone en la cola de envío.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ PRIMERO AQUÍ Y DESPUÉS AL SERVIDOR — SIEMPRE, HAYA SEÑAL O NO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Podría haber dos caminos —al servidor si hay internet, aquí si no— y sería un
 * error. El cajero no sabe en qué estado está la señal en el segundo exacto en
 * que toca «Registrar», y una pantalla que a veces guarda y a veces da error no
 * se puede aprender a usar.
 *
 * Con un solo camino, registrar SIEMPRE funciona y siempre tarda lo mismo: se
 * escribe aquí, el cliente ya se puede elegir, y el envío ocurre detrás. Es
 * exactamente cómo funcionan las ventas desde el principio.
 *
 * ⚠️ El `id` lo elige el dispositivo y el servidor lo respeta. Esa es la pieza
 * que permite que la venta guardada apunte a un cliente que todavía no existe
 * allá.
 */
export async function guardarClienteLocal(cliente: Customer): Promise<void> {
  await db.transaction("rw", db.clientes, db.clientesPendientes, async () => {
    await db.clientes.put(cliente);
    await db.clientesPendientes.put({
      id: cliente.id,
      datos: {
        client_uuid: cliente.id,
        name: cliente.name,
        phone: cliente.phone,
        tax_id_tipo: cliente.tax_id_tipo,
        tax_id: cliente.tax_id,
        address: cliente.address,
        email: cliente.email,
      },
      creado_en: new Date().toISOString(),
      intentos: 0,
      ultimo_error: null,
    });
  });
}

export async function clientesPendientes(): Promise<ClientePendiente[]> {
  return db.clientesPendientes.orderBy("creado_en").toArray();
}

export async function contarClientesPendientes(): Promise<number> {
  return db.clientesPendientes.count();
}

/** El servidor lo aceptó con el id que mandamos: sale de la cola y se refresca. */
export async function clienteAceptado(cliente: Customer): Promise<void> {
  await db.transaction("rw", db.clientes, db.clientesPendientes, async () => {
    await db.clientes.put(cliente);
    await db.clientesPendientes.delete(cliente.id);
  });
}

export async function marcarClienteFallido(id: string, error: string): Promise<void> {
  const fila = await db.clientesPendientes.get(id);
  if (!fila) return;
  await db.clientesPendientes.put({
    ...fila,
    intentos: fila.intentos + 1,
    ultimo_error: error,
  });
}

/**
 * El servidor devolvió OTRO cliente: se cambia el id en todo lo que lo nombra.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ CUÁNDO PASA ESTO, Y POR QUÉ NO SE PUEDE IGNORAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Pasa cuando esa cédula YA estaba registrada. El cajero no podía saberlo —no
 * había señal para buscar— y registró de nuevo a doña Carmen. El servidor, muy
 * correctamente, no crea una segunda ficha: devuelve la de siempre, con su
 * propio id.
 *
 * Y ahí queda una venta guardada apuntando a un cliente que ya no es. Mandarla
 * así la rechazarían con «ese cliente no existe» y el fiado de doña Carmen
 * acabaría en la bandeja de conflictos en vez de en su cuenta.
 *
 * Así que se corrige antes de mandarla: la venta pasa a nombrar al cliente de
 * verdad, y la ficha local duplicada desaparece.
 */
export async function reemplazarClienteEnLaCola(
  idViejo: string,
  real: Customer,
): Promise<void> {
  await db.transaction(
    "rw",
    db.clientes,
    db.clientesPendientes,
    db.pendientes,
    async () => {
      await db.clientes.delete(idViejo);
      await db.clientes.put(real);
      await db.clientesPendientes.delete(idViejo);

      const ventas = await db.pendientes.toArray();
      for (const venta of ventas) {
        if (venta.payload.customer_id !== idViejo) continue;
        await db.pendientes.put({
          ...venta,
          payload: { ...venta.payload, customer_id: real.id },
          recibo: { ...venta.recibo, customer_id: real.id, customer_name: real.name },
        });
      }
    },
  );
}

/**
 * Descarta un cliente que el servidor rechazó.
 *
 * ⚠️ Se borra también de la lista local, y es a propósito. Un cliente que el
 * servidor no acepta —un tipo de documento que no existe en ese país, un
 * nombre imposible— no puede quedarse enseñándose en el buscador como si fuera
 * uno más: la siguiente venta que se le haga acabaría también rechazada.
 */
export async function descartarClientePendiente(id: string): Promise<void> {
  await db.transaction("rw", db.clientes, db.clientesPendientes, async () => {
    await db.clientes.delete(id);
    await db.clientesPendientes.delete(id);
  });
}

// =============================================================================
//  COLA DE VENTAS
// =============================================================================

export async function encolarVenta(venta: {
  payload: VentaIn;
  recibo: Sale;
}): Promise<void> {
  await db.pendientes.put({
    client_uuid: venta.payload.client_uuid,
    payload: venta.payload,
    recibo: venta.recibo,
    creado_en: new Date().toISOString(),
    intentos: 0,
    ultimo_error: null,
  });
}

export async function ventasPendientes(): Promise<VentaPendiente[]> {
  return db.pendientes.orderBy("creado_en").toArray();
}

export async function contarPendientes(): Promise<number> {
  return db.pendientes.count();
}

export async function quitarPendiente(clientUuid: string): Promise<void> {
  await db.pendientes.delete(clientUuid);
}

export async function marcarIntentoFallido(
  clientUuid: string,
  error: string,
): Promise<void> {
  const fila = await db.pendientes.get(clientUuid);
  if (!fila) return;
  await db.pendientes.put({
    ...fila,
    intentos: fila.intentos + 1,
    ultimo_error: error,
  });
}

// =============================================================================
//  BANDEJA DE CONFLICTOS
// =============================================================================

/**
 * Mueve una venta rechazada a la bandeja de conflictos.
 *
 * Sale de `pendientes` y entra en `conflictos` en la misma transacción: nunca
 * puede quedar en las dos ni desaparecer de ambas.
 */
export async function moverAConflicto(
  pendiente: VentaPendiente,
  error: { code: string; message: string },
): Promise<void> {
  await db.transaction("rw", db.pendientes, db.conflictos, async () => {
    await db.conflictos.put({
      client_uuid: pendiente.client_uuid,
      payload: pendiente.payload,
      recibo: pendiente.recibo,
      error_code: error.code,
      error_message: error.message,
      fecha: new Date().toISOString(),
    });
    await db.pendientes.delete(pendiente.client_uuid);
  });
}

export async function ventasEnConflicto(): Promise<VentaEnConflicto[]> {
  return db.conflictos.orderBy("fecha").reverse().toArray();
}

export async function contarConflictos(): Promise<number> {
  return db.conflictos.count();
}

/** Devuelve una venta en conflicto a la cola, para volver a intentarla. */
export async function reintentarConflicto(clientUuid: string): Promise<void> {
  await db.transaction("rw", db.pendientes, db.conflictos, async () => {
    const fila = await db.conflictos.get(clientUuid);
    if (!fila) return;

    await db.pendientes.put({
      client_uuid: fila.client_uuid,
      payload: fila.payload,
      recibo: fila.recibo,
      creado_en: new Date().toISOString(),
      intentos: 0,
      ultimo_error: null,
    });
    await db.conflictos.delete(clientUuid);
  });
}

/**
 * Descarta definitivamente una venta en conflicto.
 *
 * Es la única operación de Softfi que pierde información a propósito, así que la
 * interfaz la pide con confirmación explícita y avisando que ese dinero quedará
 * sin registrar.
 */
export async function descartarConflicto(clientUuid: string): Promise<void> {
  await db.conflictos.delete(clientUuid);
}
